import React from 'react';
import { func, bool, shape } from 'prop-types';
import { connect } from 'react-redux';
import { Field, Form } from 'react-final-form';
import {
  Button,
  CondensedMedium,
  DataTest,
  FluidButtonLayout,
  Gutter,
  Label,
  TaxonomyUtil,
  useApi,
} from '@axiom/ui';
import isEqual from 'lodash/fp/isEqual';

import MultiSelect from '../MultiSelect/MultiSelect';
import UserMultiSelect from '../UserMultiSelect/UserMultiSelect';
import StoreStateTreatment from '../StoreStateTreatment/StoreStateTreatment';
import SearchInput from '../SearchInput/SearchInput';
import DatePicker from '../DatePicker/DatePicker';
import { FFInput } from '../FFInput/FFInput';
import { EditSavedOpportunityFilterStore } from '../../stores/edit-saved-opportunity-filter-store';
import { OpportunitiesStore } from '../../stores/opportunities-store';
import { PreloadedAppErrorsStore } from '../../stores/preloaded-app-errors-store';
import { formatDataTestId } from '../../utils/data-test-util';
import { getTreeTrunk } from '../../utils/dropdown-tree-util';
import {
  containsInvalidCharacters,
  invalidCharactersErrorMessage,
} from '../../utils/search-util';
import { TaxonomyApi } from '../../api/taxonomy-api';

import {
  opportunityStageSelectItems,
  opportunityBusinessTeamSelectItems,
} from './opportunity-filters-form-const';
import {
  OFFDateRangeContainer,
  OFFMultiSelectContainer,
} from './OpportunityFiltersFormStyles';

const defaultFilters = OpportunitiesStore.getDefaultFilters();

const OpportunityFiltersForm = ({
  savedFiltersState,
  filters,
  handleFilterChange,
  handleSaveFilters,
  isEditable,
}) => {
  const [{ data: taxonomy }] = useApi(TaxonomyApi.readTaxonomy());
  const practiceAreaOptions = TaxonomyUtil.getPracticeAreaOptions(
    taxonomy.practiceAreas
  );
  return (
    <DataTest value="OpportunityFiltersForm">
      <SearchInput
        aria-label="Search Opportunities"
        name="search"
        value={filters.search || ''}
        onChange={e => {
          const invalidCharacters = containsInvalidCharacters(e.target.value);
          if (invalidCharacters) {
            PreloadedAppErrorsStore.show(invalidCharactersErrorMessage);
            return false;
          }
          return handleFilterChange({
            search: e.target.value,
          });
        }}
        disabled={!isEditable}
        data-test={formatDataTestId('searchBar')}
      />
      <Label>Opportunity Stage</Label>
      <OFFMultiSelectContainer>
        <MultiSelect
          options={opportunityStageSelectItems}
          isDisabled={!isEditable}
          name="stageCode"
          onChange={selectedOptions => {
            handleFilterChange({
              stageCode: selectedOptions,
            });
          }}
          placeholder="Show All"
          value={filters.stageCode}
        />
      </OFFMultiSelectContainer>
      <Label>Business Team</Label>
      <OFFMultiSelectContainer>
        <MultiSelect
          options={opportunityBusinessTeamSelectItems}
          disabled={!isEditable}
          name="businessTeam"
          onChange={selectedOptions => {
            handleFilterChange({
              businessTeam: selectedOptions,
            });
          }}
          placeholder="Show All"
          value={filters.businessTeam}
        />
      </OFFMultiSelectContainer>
      <Label>TS Lead</Label>
      <OFFMultiSelectContainer>
        <UserMultiSelect
          disabled={!isEditable}
          name="ownerUserId"
          onChange={selectedUsers => {
            handleFilterChange({
              ownerUserId: selectedUsers,
            });
          }}
          placeholder="Show All"
          value={filters.ownerUserId}
        />
      </OFFMultiSelectContainer>

      <Label>Sales Owner</Label>
      <OFFMultiSelectContainer>
        <UserMultiSelect
          disabled={!isEditable}
          name="salesLeadId"
          onChange={selectedUsers => {
            handleFilterChange({
              salesLeadId: selectedUsers,
            });
          }}
          placeholder="Show All"
          value={filters.salesLeadId}
        />
      </OFFMultiSelectContainer>

      <Label>Practice Area</Label>
      <OFFMultiSelectContainer>
        <MultiSelect
          options={practiceAreaOptions}
          disabled={!isEditable}
          name="practiceAreas"
          onChange={selectedPracticeAreas => {
            handleFilterChange({
              practiceAreas: selectedPracticeAreas,
            });
          }}
          placeholder="Show All"
          value={filters.practiceAreas}
        />
      </OFFMultiSelectContainer>
      <Label>Close Date Range</Label>
      <OFFDateRangeContainer>
        <DataTest value="CloseDateStart" style={{ flex: 1 }} inline>
          <DatePicker
            disableParentScrolling={false}
            name="salesCloseDate"
            id="CloseDateRange1"
            openDirection="up"
            readOnly
            showClearDate
            placeholder="Any Date"
            date={filters.salesCloseDateStart}
            disabled={!isEditable}
            onChange={salesCloseDateStart => {
              handleFilterChange({ salesCloseDateStart });
            }}
          />
        </DataTest>
        <Gutter as="span" horizontal="1rem">
          <CondensedMedium>to</CondensedMedium>
        </Gutter>
        <DataTest value="CloseDateEnd" style={{ flex: 1 }} inline>
          <DatePicker
            disableParentScrolling={false}
            name="salesCloseDate"
            id="CloseDateRange2"
            openDirection="up"
            readOnly
            showClearDate
            placeholder="Any Date"
            anchorDirection="right"
            date={filters.salesCloseDateEnd}
            disabled={!isEditable}
            onChange={salesCloseDateEnd => {
              handleFilterChange({ salesCloseDateEnd });
            }}
          />
        </DataTest>
      </OFFDateRangeContainer>
      {!!isEditable && (
        <StoreStateTreatment
          storeState={savedFiltersState}
          renderNonLoadedView={() => (
            <Form
              onSubmit={data => {
                handleSaveFilters(data);
              }}
              initialValues={filters}
              render={({ handleSubmit }) => (
                <form noValidate onSubmit={handleSubmit}>
                  <Field
                    name="name"
                    domID="save-name"
                    component={FFInput}
                    data-test={formatDataTestId('opportunityEditClientName')}
                    validate={value =>
                      value ? undefined : 'Please specify a name before saving'
                    }
                    label="Search Name"
                  />
                  <FluidButtonLayout>
                    <Button
                      name="RESETFILTERSBUTTON"
                      disabled={isEqual(filters, defaultFilters)}
                      onClick={() => OpportunitiesStore.resetFilters()}
                      variation="outline"
                    >
                      Reset Filters
                    </Button>
                    <Button name="SAVESEARCHBUTTON" onClick={handleSubmit}>
                      Save Search
                    </Button>
                  </FluidButtonLayout>
                </form>
              )}
            />
          )}
        />
      )}
    </DataTest>
  );
};

OpportunityFiltersForm.defaultProps = {
  handleFilterChange: () => {},
};

OpportunityFiltersForm.propTypes = {
  filters: shape({}).isRequired,
  handleFilterChange: func,
  handleSaveFilters: func.isRequired,
  isEditable: bool,
  savedFiltersState: EditSavedOpportunityFilterStore.getStateShape().isRequired,
};

export default connect(state => ({
  savedFiltersState: EditSavedOpportunityFilterStore.select(state),
}))(OpportunityFiltersForm);
